import React from 'react';
import { Icon } from '@iq/react-components';

import Tooltip from '../../Tooltip/Tooltip';
import { SeverityCell } from './AlarmCells';
import { ALARM_TABS, UserSignature } from './utils';
import config from '../../../config';

const HeaderWithTooltip = ({ title, tooltip }) => (
  <span className="alarm-header-with-tooltip">
    {title}
    <Tooltip
      content={tooltip}
      placement="top"
    >
      <Icon
        icon="he-info"
        size="xs"
      />
    </Tooltip>
  </span>
);

const cellStyle = { textAlign: 'left' };

export const OPTIONAL_ALARM_COLUMNS = [
  { id: 'highestSeverity', title: 'Highest' },
  { id: 'latestSeverity', title: 'Latest' },
  { id: 'deviatingReferenceDesignation', title: 'Equipment' },
  { id: 'acknowledgedBy', title: 'Acknowledged By' },
  { id: 'resolvedBy', title: 'Resolved By' },
];

// Base column definitions (required or reusable)
const highestSeverityCol = {
  id: 'highestSeverity',
  Header: () => (
    <HeaderWithTooltip
      title="Highest"
      tooltip="The highest severity reported for an instance of a specific alarm since any prior resolution"
    />
  ),
  accessor: 'highestSeverity',
  className: 'alarm-cell',
  style: { ...cellStyle },
  sortable: true,
  width: 100,
  minWidth: 50,
  Cell: ({ cell, row }) => (
    <SeverityCell
      severity={cell.value}
      color={row.original.highestSeverityColor}
    />
  ),
};

const latestSeverityCol = {
  id: 'latestSeverity',
  Header: () => (
    <HeaderWithTooltip
      title="Latest"
      tooltip="The latest severity for an instance of a specific alarm"
    />
  ),
  accessor: 'latestSeverity',
  className: 'alarm-cell',
  style: { ...cellStyle },
  sortable: true,
  width: 100,
  minWidth: 50,
  Cell: ({ cell, row }) => (
    <SeverityCell
      severity={cell.value}
      color={row.original.latestSeverityColor}
    />
  ),
};

const algorithmNameCol = {
  id: 'algorithmName',
  Header: 'Alarm Name',
  accessor: 'displayAlarmName',
  className: 'alarm-cell',
  style: { ...cellStyle },
  sortable: true,
  width: 200,
  minWidth: 50,
};

const equipmentCol = {
  id: 'deviatingReferenceDesignation',
  Header: 'Equipment',
  accessor: 'displayEquipment',
  className: 'alarm-cell',
  style: { ...cellStyle },
  width: 100,
  minWidth: 50,
  sortable: true,
};

const lastOccurrenceCol = {
  id: 'lastOccurrence',
  Header: 'Last Occurrence',
  accessor: 'formattedLastOccurrence',
  className: 'alarm-cell',
  style: { ...cellStyle },
  sortable: true,
  width: 80,
  minWidth: 50,
};

const acknowledgedByCol = {
  id: 'acknowledgedBy',
  Header: 'Acknowledged By',
  accessor: 'acknowledgedBy',
  className: 'alarm-cell',
  minWidth: 120,
  width: 150,
  sortable: true,
  Cell: ({ row }) => <UserSignature userId={row.original.acknowledgedBy} />,
};

const acknowledgedAtCol = {
  id: 'acknowledgedAt',
  Header: 'Acknowledged At',
  accessor: 'formattedAcknowledgedAt',
  className: 'alarm-cell',
  minWidth: 150,
  width: 150,
  sortable: true,
};

const resolvedByCol = {
  id: 'resolvedBy',
  Header: 'Resolved By',
  accessor: 'resolvedBy',
  className: 'alarm-cell',
  minWidth: 80,
  width: 150,
  sortable: true,
  Cell: ({ row }) => <UserSignature userId={row.original.resolvedBy} />,
};

const resolvedAtCol = {
  id: 'resolvedAt',
  Header: 'Resolved At',
  accessor: 'formattedResolvedAt',
  className: 'alarm-cell',
  minWidth: 80,
  width: 150,
  sortable: true,
};

// Build final columns for a tab based on excluded optional columns and onPrem flag
// excludedColumns: array of column ids to hide; empty = show all
export const getColumnsForTab = (activeTab, excludedColumns = [], onPrem = config.onPremise) => {
  // A column is visible unless it is explicitly excluded or suppressed by onPrem
  const include = (colId) => {
    if (onPrem && (colId === 'acknowledgedBy' || colId === 'resolvedBy')) return false;
    return !excludedColumns.includes(colId);
  };

  switch (activeTab) {
    case ALARM_TABS.NEW: {
      const cols = [
        include('highestSeverity') ? highestSeverityCol : null,
        include('latestSeverity') ? latestSeverityCol : null,
        algorithmNameCol,
        include('deviatingReferenceDesignation') ? equipmentCol : null,
        lastOccurrenceCol,
      ].filter(Boolean);
      return cols;
    }
    case ALARM_TABS.ACKNOWLEDGED: {
      const cols = [
        include('highestSeverity') ? highestSeverityCol : null,
        include('latestSeverity') ? latestSeverityCol : null,
        algorithmNameCol,
        include('acknowledgedBy') ? acknowledgedByCol : null,
        acknowledgedAtCol,
      ].filter(Boolean);
      return cols;
    }
    default: {
      const cols = [
        algorithmNameCol,
        include('deviatingReferenceDesignation') ? equipmentCol : null,
        include('resolvedBy') ? resolvedByCol : null,
        resolvedAtCol,
      ].filter(Boolean);
      return cols;
    }
  }
};
